import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { MDSButton } from "@/mds";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import {
  MdsIconButton,
  MdsIconButtonSize,
  MdsIconButtonVariant,
} from "@/design-system/components/icon-button";
import { JsonObject } from "@/domains/common/types";
import { toJS } from "mobx";
import { FloatingNotePreview } from "@/design-system/components/item-list/rows/item-preview/FloatingNotePreview";
import { HoveringState } from "@/design-system/components/item-list/rows/item-preview/HoveringState";
import { StyledLabel } from "@/mds/components/forms/label/label.style";
import { css } from "@/domains/emotion";
import { MdsTooltip, MdsTooltipPlacement } from "@/design-system/components/tooltip";
import { AnimatePresence, motion } from "motion/react";

type CandidateCollectionItem = {
  id: string;
  name: string;
  member_note_ids: string[];
  candidate_note_ids: string[];
};

interface AutoOrganizeUpdateCollectionsFormProps {
  requestId: string;
  sessionId: string;
  dismissAutoOrganizeUpdateCollectionsSession: () => void;
  metadata: JsonObject;
}

export const AutoOrganizeUpdateCollectionsForm = observer(
  ({
    requestId,
    sessionId,
    dismissAutoOrganizeUpdateCollectionsSession,
    metadata,
  }: AutoOrganizeUpdateCollectionsFormProps) => {
    const { store } = useAppStore();
    const initialCandidateCollections: CandidateCollectionItem[] = Array.isArray(
      metadata.candidate_collections
    )
      ? (metadata.candidate_collections as CandidateCollectionItem[])
      : [];
    const initialUncategorized = (metadata.uncategorized_note_ids || []) as string[];
    const [candidateCollections, setCandidateCollections] = useState<CandidateCollectionItem[]>([
      ...initialCandidateCollections,
    ]);
    const [uncategorized, setUncategorized] = useState<Set<string>>(new Set(initialUncategorized));
    const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
    const MAX_VISIBLE = 3;
    const [hoveredNote, setHoveredNote] = useState<string | null>(null);
    const [hoverState] = useState(() => new HoveringState());
    const previewTimeoutRef = useRef<number | null>(null);
    const [showAllNotesMap, setShowAllNotesMap] = useState<Record<string, boolean>>({});

    const totalCandidateNotes = candidateCollections.reduce(
      (sum, col) => sum + col.candidate_note_ids.length,
      0
    );

    const summaryText = useMemo(() => {
      const totalNotesBeingAdded = candidateCollections.reduce(
        (sum, col) => sum + (col.candidate_note_ids || []).length,
        0
      );

      if (totalNotesBeingAdded === 0) {
        return "I will not add any notes to any collections.";
      }

      return `I will organize ${totalNotesBeingAdded} note${
        totalNotesBeingAdded !== 1 ? "s" : ""
      } into ${candidateCollections.length} collection${
        candidateCollections.length !== 1 ? "s" : ""
      }`;
    }, [candidateCollections]);

    // Helper to get note title from id
    const getNoteTitle = (noteId: string) => {
      const note = store.notes.getNoteObservableById({ noteId });
      return note?.title || "Untitled";
    };

    // Remove candidate note from a collection
    const handleRemoveCandidateNote = (collectionIdx: number, noteId: string) => {
      setCandidateCollections(prev => {
        const updated = prev.map((col, idx) =>
          idx === collectionIdx
            ? { ...col, candidate_note_ids: col.candidate_note_ids.filter(id => id !== noteId) }
            : col
        );
        // If note is not in any candidate_note_ids of any collection, add to uncategorized
        const stillInCollections = updated.some(col => col.candidate_note_ids.includes(noteId));
        setUncategorized(prevUncat => {
          const newSet = new Set(prevUncat);
          if (!stillInCollections) newSet.add(noteId);
          return newSet;
        });
        return updated;
      });
    };

    // Expand/collapse collection
    const handleToggleExpand = useCallback((collectionId: string) => {
      setExpandedCollections(prev => {
        const newSet = new Set(prev);
        if (newSet.has(collectionId)) {
          newSet.delete(collectionId);
        } else {
          newSet.add(collectionId);
        }
        return newSet;
      });
    }, []);

    const handleMouseEnter = useCallback(
      (noteId: string, target: HTMLElement) => {
        if (previewTimeoutRef.current) {
          window.clearTimeout(previewTimeoutRef.current);
        }
        previewTimeoutRef.current = window.setTimeout(() => {
          setHoveredNote(noteId);
          hoverState.hoveredRect = target.getBoundingClientRect();
          hoverState.handleMouseEnter();
        }, 50);
      },
      [hoverState]
    );

    const handleMouseLeave = useCallback(() => {
      if (previewTimeoutRef.current) {
        window.clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      setHoveredNote(null);
      hoverState.hoveredRect = null;
      hoverState.handleMouseLeave();
    }, [hoverState]);

    useEffect(() => {
      return () => {
        if (previewTimeoutRef.current) {
          window.clearTimeout(previewTimeoutRef.current);
        }
      };
    }, []);

    const toggleShowAllNotes = useCallback((collectionId: string) => {
      setShowAllNotesMap(prev => ({
        ...prev,
        [collectionId]: !prev[collectionId],
      }));
    }, []);

    // Submit handler
    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        await store.sessionEvents.createInputResponseSessionEvent({
          sessionId,
          config: {},
          requestId,
          metadata: {
            ...metadata,
            candidate_collections: candidateCollections,
            uncategorized_note_ids: Array.from(uncategorized),
          },
          inputKind: "AUTO_ORGANIZE_UPDATE_COLLECTIONS",
        });
        await store.sessionEvents.createMessageSentSessionEvent({
          session_id: sessionId,
          message_value: {
            kind: "COMMUNICATION",
            content: summaryText,
          },
        });
      },
      [
        candidateCollections,
        uncategorized,
        store.sessionEvents,
        sessionId,
        requestId,
        metadata,
        summaryText,
      ]
    );

    return (
      <FormContainer onSubmit={handleSubmit}>
        <StyledLabel>
          I found {totalCandidateNotes} note{totalCandidateNotes !== 1 ? "s" : ""} that belong in
          your existing collections. Review the suggestions below. Next, I&apos;ll suggest new
          collections for the remaining notes.
        </StyledLabel>
        <ProgressIndicator step={1} />
        {candidateCollections.map((col, idx) => {
          const isExpanded = expandedCollections.has(col.id);
          const showAllNotes = showAllNotesMap[col.id];
          const visibleMemberNotes = isExpanded
            ? showAllNotes
              ? col.member_note_ids
              : col.member_note_ids.slice(0, MAX_VISIBLE)
            : [];
          const visibleCandidateNotes = isExpanded
            ? showAllNotes
              ? col.candidate_note_ids
              : col.candidate_note_ids.slice(0, MAX_VISIBLE)
            : [];
          const totalNotes = col.member_note_ids.length + col.candidate_note_ids.length;
          const hasMoreNotes =
            (col.member_note_ids.length > MAX_VISIBLE ||
              col.candidate_note_ids.length > MAX_VISIBLE) &&
            !showAllNotes;

          return (
            <DrawerCard key={col.id}>
              <DrawerHeader onClick={(_e: React.MouseEvent) => handleToggleExpand(col.id)}>
                <DrawerTitleBlock>
                  <DrawerTitle $isExpanded={isExpanded} $isEditable={false}>
                    {col.name}
                  </DrawerTitle>
                  <DrawerSubtitle>
                    {col.candidate_note_ids.length} note
                    {col.candidate_note_ids.length !== 1 ? "s" : ""} to add
                  </DrawerSubtitle>
                </DrawerTitleBlock>
                <div style={{ marginLeft: 12 }}>
                  <MdsIconButton
                    type="button"
                    iconKind={isExpanded ? MdsIconKind.AngleUp : MdsIconKind.AngleDown}
                    size={MdsIconButtonSize.Small}
                    variant={MdsIconButtonVariant.Transparent}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    onClick={(_e: React.MouseEvent) => {
                      _e.stopPropagation();
                      handleToggleExpand(col.id);
                    }}
                  />
                </div>
              </DrawerHeader>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className={css({
                      padding: "0px 8px 0px 8px",
                      overflowY: "scroll",
                      background: "#fff",
                      borderTop: "none",
                      marginBottom: 4,
                    })}
                  >
                    {/* Readonly member notes */}
                    {visibleMemberNotes.map(noteId => (
                      <ReadonlyNoteRow
                        key={noteId}
                        onMouseEnter={e => handleMouseEnter(noteId, e.currentTarget)}
                        onMouseLeave={handleMouseLeave}
                        style={{ opacity: 0.5 }}
                      >
                        <MdsIcon
                          kind={MdsIconKind.Document}
                          className={css({
                            marginRight: 6,
                            fontSize: 14,
                            color: "#9397A5",
                            flexShrink: 0,
                          })}
                        />
                        <NoteTitle>{getNoteTitle(noteId)}</NoteTitle>
                      </ReadonlyNoteRow>
                    ))}
                    {/* Editable candidate notes */}
                    {visibleCandidateNotes.map(noteId => (
                      <NoteRow
                        key={noteId}
                        onMouseEnter={e => handleMouseEnter(noteId, e.currentTarget)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <MdsIcon
                          kind={MdsIconKind.Document}
                          className={css({
                            marginRight: 6,
                            fontSize: 14,
                            color: "#9397A5",
                            flexShrink: 0,
                          })}
                        />
                        <NoteTitle>{getNoteTitle(noteId)}</NoteTitle>
                        <MdsTooltip
                          config={{
                            label: "Remove suggestion",
                            placement: MdsTooltipPlacement.Top,
                          }}
                        >
                          <RemoveButton
                            type="button"
                            onClick={(_e: React.MouseEvent) =>
                              handleRemoveCandidateNote(idx, noteId)
                            }
                          >
                            <MdsIcon kind={MdsIconKind.CircleMinus} />
                          </RemoveButton>
                        </MdsTooltip>
                      </NoteRow>
                    ))}
                    {isExpanded && hasMoreNotes && (
                      <SeeMoreButton onClick={(_e: React.MouseEvent) => toggleShowAllNotes(col.id)}>
                        See{" "}
                        {totalNotes - (visibleMemberNotes.length + visibleCandidateNotes.length)}{" "}
                        more note
                        {totalNotes - (visibleMemberNotes.length + visibleCandidateNotes.length) !==
                        1
                          ? "s"
                          : ""}
                      </SeeMoreButton>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </DrawerCard>
          );
        })}
        {uncategorized.size > 0 && (
          <DrawerCard>
            <DrawerHeader
              onClick={(_e: React.MouseEvent) => handleToggleExpand("-1")}
              style={{ cursor: "pointer" }}
            >
              <DrawerTitleBlock>
                <DrawerTitle $isExpanded={expandedCollections.has("-1")} $isEditable={false}>
                  Remaining notes
                </DrawerTitle>
                <DrawerSubtitle>
                  {uncategorized.size} note{uncategorized.size !== 1 ? "s" : ""}
                </DrawerSubtitle>
              </DrawerTitleBlock>
              <div style={{ marginLeft: 12 }}>
                <MdsIconButton
                  type="button"
                  iconKind={
                    expandedCollections.has("-1") ? MdsIconKind.AngleUp : MdsIconKind.AngleDown
                  }
                  size={MdsIconButtonSize.Small}
                  variant={MdsIconButtonVariant.Transparent}
                  aria-label={expandedCollections.has("-1") ? "Collapse" : "Expand"}
                  onClick={(_e: React.MouseEvent) => {
                    _e.stopPropagation();
                    handleToggleExpand("-1");
                  }}
                />
              </div>
            </DrawerHeader>
            <AnimatePresence>
              {expandedCollections.has("-1") && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className={css({
                    padding: "0px 8px 0px 8px",
                    overflowY: "scroll",
                    background: "#fff",
                    borderTop: "none",
                  })}
                >
                  {Array.from(uncategorized)
                    .slice(0, expandedCollections.has("-1") ? undefined : MAX_VISIBLE)
                    .map(noteId => (
                      <NoteRow
                        key={noteId}
                        $isNew={true}
                        onMouseEnter={e => handleMouseEnter(noteId, e.currentTarget)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <MdsIcon
                          kind={MdsIconKind.Document}
                          className={css({
                            marginRight: 6,
                            fontSize: 14,
                            color: "#9397A5",
                            flexShrink: 0,
                          })}
                        />
                        <NoteTitle>{getNoteTitle(noteId)}</NoteTitle>
                      </NoteRow>
                    ))}
                  {uncategorized.size > MAX_VISIBLE && !expandedCollections.has("-1") && (
                    <Ellipsis>...and {uncategorized.size - MAX_VISIBLE} more</Ellipsis>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </DrawerCard>
        )}
        {hoveredNote && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={store.notes.getNoteObservableById({ noteId: hoveredNote })}
            placement="right"
          />
        )}
        <SummaryRow>
          {/* <SummaryText>{summaryText}</SummaryText> */}
          {/* {uncategorized.size > 0 && (
            <UncategorizedCount>
              {uncategorized.size} note{uncategorized.size !== 1 ? "s" : ""} left to organize
            </UncategorizedCount>
          )} */}
          <ButtonGroup>
            <MDSButton
              type="button"
              color="white"
              size="md"
              onClick={dismissAutoOrganizeUpdateCollectionsSession}
            >
              Done organizing
            </MDSButton>
            <MDSButton type="submit" color="contrast" size="md">
              Confirm
            </MDSButton>
          </ButtonGroup>
        </SummaryRow>
      </FormContainer>
    );
  }
);

// Add a local type for the extended collection item
interface EditableCandidateCollectionItem extends CandidateCollectionItem {
  state: "accepted" | "rejected";
  refinement_phrase: string;
  editing: boolean;
}

interface AutoOrganizeCreateCollectionsFormProps {
  requestId: string;
  sessionId: string;
  dismissAutoOrganizeCreateCollectionsSession: () => void;
  metadata: JsonObject;
}

export const AutoOrganizeCreateCollectionsForm = observer(
  ({
    requestId,
    sessionId,
    dismissAutoOrganizeCreateCollectionsSession,
    metadata,
  }: AutoOrganizeCreateCollectionsFormProps) => {
    const { store } = useAppStore();
    console.log("metadata", toJS(metadata));
    // Deep copy to allow editing
    const initialCandidateCollections: EditableCandidateCollectionItem[] = Array.isArray(
      metadata.candidate_collections
    )
      ? (metadata.candidate_collections as CandidateCollectionItem[]).map(col => ({
          ...col,
          member_note_ids: [...(col.member_note_ids || []), ...(col.candidate_note_ids || [])],
          state: "accepted",
          refinement_phrase: "",
          editing: false,
        }))
      : [];
    const [collections, setCollections] = useState<EditableCandidateCollectionItem[]>(
      initialCandidateCollections
    );
    const [uncategorized, setUncategorized] = useState<Set<string>>(new Set());
    const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
    const [hoveredNote, setHoveredNote] = useState<string | null>(null);
    const [hoverState] = useState(() => new HoveringState());
    const previewTimeoutRef = useRef<number | null>(null);
    const MAX_VISIBLE = 3;
    const [showAllNotesMap, setShowAllNotesMap] = useState<Record<string, boolean>>({});

    const allNoteIds = useMemo(() => {
      const notes = new Set<string>();
      if (Array.isArray(metadata.candidate_collections)) {
        (metadata.candidate_collections as CandidateCollectionItem[]).forEach(col => {
          (col.member_note_ids || []).forEach(id => notes.add(id));
          (col.candidate_note_ids || []).forEach(id => notes.add(id));
        });
      }
      ((metadata.uncategorized_note_ids || []) as string[]).forEach(id => notes.add(id));
      return notes;
    }, [metadata.candidate_collections, metadata.uncategorized_note_ids]);

    useEffect(() => {
      const notesInAcceptedCollections = new Set<string>();
      collections.forEach(col => {
        if (col.state === "accepted") {
          (col.member_note_ids || []).forEach(id => {
            notesInAcceptedCollections.add(id);
          });
        }
      });

      const newUncategorized = new Set<string>();
      for (const noteId of allNoteIds) {
        if (!notesInAcceptedCollections.has(noteId)) {
          newUncategorized.add(noteId);
        }
      }
      setUncategorized(newUncategorized);
    }, [collections, allNoteIds]);

    // Handle drawer expansion
    const handleToggleExpand = useCallback((collectionIdx: number | string) => {
      const collectionId = collectionIdx.toString();
      setExpandedCollections(prev => {
        const newSet = new Set(prev);
        if (newSet.has(collectionId)) {
          newSet.delete(collectionId);
        } else {
          newSet.add(collectionId);
        }
        return newSet;
      });
    }, []);

    // Accept/Reject logic
    const handleToggleState = (idx: number) => {
      setCollections(prev =>
        prev.map((col, i) =>
          i === idx ? { ...col, state: col.state === "accepted" ? "rejected" : "accepted" } : col
        )
      );
    };

    // Edit collection name
    const handleEditName = (idx: number, name: string) => {
      setCollections(prev => prev.map((col, i) => (i === idx ? { ...col, name } : col)));
    };
    const handleStartEdit = (idx: number) => {
      setCollections(prev => prev.map((col, i) => (i === idx ? { ...col, editing: true } : col)));
    };
    const handleEndEdit = (idx: number) => {
      setCollections(prev => prev.map((col, i) => (i === idx ? { ...col, editing: false } : col)));
    };

    // Feedback
    const handleFeedback = (idx: number, value: string) => {
      setCollections(prev =>
        prev.map((col, i) => (i === idx ? { ...col, refinement_phrase: value } : col))
      );
    };

    // Drag and drop logic
    const [dragged, setDragged] = useState<{ noteId: string; fromIdx: number } | null>(null);

    const handleDragStart = (noteId: string, fromIdx: number, e: React.DragEvent) => {
      e.stopPropagation();
      setDragged({ noteId, fromIdx });
    };

    const handleDrop = (toIdx: number, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragged || dragged.fromIdx === toIdx) return;
      setCollections(prev => {
        const fromCol = { ...prev[dragged.fromIdx] };
        const toCol = { ...prev[toIdx] };
        fromCol.member_note_ids = fromCol.member_note_ids.filter(id => id !== dragged.noteId);
        toCol.member_note_ids = [...toCol.member_note_ids, dragged.noteId];
        const updated = prev.map((col, i) =>
          i === dragged.fromIdx ? fromCol : i === toIdx ? toCol : col
        );
        return updated;
      });
      setDragged(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Hover preview logic
    const handleMouseEnter = useCallback(
      (noteId: string, target: HTMLElement) => {
        if (previewTimeoutRef.current) {
          window.clearTimeout(previewTimeoutRef.current);
        }
        previewTimeoutRef.current = window.setTimeout(() => {
          setHoveredNote(noteId);
          hoverState.hoveredRect = target.getBoundingClientRect();
          hoverState.handleMouseEnter();
        }, 50);
      },
      [hoverState]
    );
    const handleMouseLeave = useCallback(() => {
      if (previewTimeoutRef.current) {
        window.clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      setHoveredNote(null);
      hoverState.hoveredRect = null;
      hoverState.handleMouseLeave();
    }, [hoverState]);
    useEffect(() => {
      return () => {
        if (previewTimeoutRef.current) {
          window.clearTimeout(previewTimeoutRef.current);
        }
      };
    }, []);

    // Helper
    const getNoteTitle = (noteId: string) => {
      const note = store.notes.getNoteObservableById({ noteId });
      return note?.title || "Untitled";
    };

    const toggleShowAllNotes = useCallback((idx: number) => {
      setShowAllNotesMap(prev => ({
        ...prev,
        [idx]: !prev[idx],
      }));
    }, []);

    // Add remove note handler
    const handleRemoveNote = useCallback((collectionIdx: number, noteId: string) => {
      setCollections(prev =>
        prev.map((col, idx) => {
          if (idx === collectionIdx) {
            const updatedNotes = (col.member_note_ids || []).filter(id => id !== noteId);
            return {
              ...col,
              member_note_ids: updatedNotes,
              state: updatedNotes.length === 0 ? "rejected" : col.state,
            };
          }
          return col;
        })
      );
    }, []);

    const summaryText = useMemo(() => {
      const acceptedCount = collections.filter(col => col.state === "accepted").length;
      const rejectedWithFeedbackCount = collections.filter(
        col => col.state === "rejected" && col.refinement_phrase.trim()
      ).length;
      const rejectedWithoutFeedbackCount = collections.filter(
        col => col.state === "rejected" && !col.refinement_phrase.trim()
      ).length;

      if (
        acceptedCount === 0 &&
        rejectedWithFeedbackCount === 0 &&
        rejectedWithoutFeedbackCount === 0
      ) {
        return "I will not create any collections.";
      }

      const parts: string[] = [];
      if (acceptedCount > 0) {
        parts.push(`create ${acceptedCount} collection${acceptedCount !== 1 ? "s" : ""}`);
      }
      if (rejectedWithFeedbackCount > 0) {
        parts.push(
          `refine ${rejectedWithFeedbackCount} collection${
            rejectedWithFeedbackCount !== 1 ? "s" : ""
          }`
        );
      }
      if (rejectedWithoutFeedbackCount > 0) {
        parts.push(
          `reject ${rejectedWithoutFeedbackCount} collection${
            rejectedWithoutFeedbackCount !== 1 ? "s" : ""
          }`
        );
      }

      return `I will ${parts.join(" and ")}.`;
    }, [collections]);

    // Submit handler
    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        // Only include accepted collections in the payload
        const payloadCollections = collections
          .filter(col => col.state === "accepted")
          .map(({ editing: _editing, state: _state, ...col }) => col);

        await store.sessionEvents.createInputResponseSessionEvent({
          sessionId,
          config: {},
          requestId,
          metadata: {
            ...metadata,
            candidate_collections: payloadCollections,
            uncategorized_note_ids: Array.from(uncategorized),
          },
          inputKind: "AUTO_ORGANIZE_CREATE_COLLECTIONS",
        });

        await store.sessionEvents.createMessageSentSessionEvent({
          session_id: sessionId,
          message_value: {
            kind: "COMMUNICATION",
            content: summaryText,
          },
        });
      },
      [collections, uncategorized, store.sessionEvents, sessionId, requestId, metadata, summaryText]
    );

    // Summary
    return (
      <FormContainer onSubmit={handleSubmit}>
        <StyledLabel>
          I&apos;ve grouped your remaining notes into collections. Reject any you don&apos;t want.
          I&apos;ll then create the rest and retry grouping the remaining notes.
        </StyledLabel>
        <ProgressIndicator step={2} />
        {collections.map((col, idx) => {
          const isExpanded = expandedCollections.has(idx.toString());
          const isRejected = col.state === "rejected";
          const showAllNotes = showAllNotesMap[idx];
          const visibleNotes = isExpanded
            ? showAllNotes
              ? col.member_note_ids || []
              : (col.member_note_ids || []).slice(0, MAX_VISIBLE)
            : [];
          const hasMoreNotes = (col.member_note_ids || []).length > MAX_VISIBLE && !showAllNotes;

          return (
            <DrawerCard key={idx} onDragOver={handleDragOver} onDrop={e => handleDrop(idx, e)}>
              <DrawerHeader
                onClick={(_e: React.MouseEvent) => handleToggleExpand(idx)}
                style={{ alignItems: "center" }}
              >
                <div style={{ flex: 1 }}>
                  {col.editing ? (
                    <input
                      value={col.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleEditName(idx, e.target.value)
                      }
                      onBlur={() => handleEndEdit(idx)}
                      autoFocus
                      style={{
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "20px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 4,
                        marginLeft: -5,
                      }}
                      onClick={(_e: React.MouseEvent) => _e.stopPropagation()}
                    />
                  ) : (
                    <DrawerTitle
                      $isExpanded={isExpanded}
                      $isRejected={isRejected}
                      $isEditable={true}
                      onClick={(_e: React.MouseEvent) => {
                        _e.stopPropagation();
                        handleStartEdit(idx);
                      }}
                    >
                      {col.name}
                    </DrawerTitle>
                  )}
                  <DrawerNoteCount>
                    {(col.member_note_ids || []).length} note
                    {(col.member_note_ids || []).length !== 1 ? "s" : ""}
                  </DrawerNoteCount>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                    marginLeft: 4,
                  }}
                >
                  <div style={{ display: "flex", gap: 4 }}>
                    <MdsIconButton
                      type="button"
                      iconKind={MdsIconKind.Check}
                      size={MdsIconButtonSize.Small}
                      variant={MdsIconButtonVariant.Transparent}
                      aria-label="Accept collection"
                      onClick={(_e: React.MouseEvent) => {
                        _e.stopPropagation();
                        if (isRejected) {
                          handleToggleState(idx);
                        }
                      }}
                      className={css({
                        backgroundColor: !isRejected ? "#e6f9ed" : "transparent",
                        color: !isRejected ? "#1a7f37" : "#6b7280",
                        "&:hover": {
                          backgroundColor: !isRejected ? "#d1f2d8" : "#f0f1f3",
                        },
                      })}
                    />
                    <MdsIconButton
                      type="button"
                      iconKind={MdsIconKind.Exit}
                      size={MdsIconButtonSize.Small}
                      variant={MdsIconButtonVariant.Transparent}
                      aria-label="Reject collection"
                      onClick={(_e: React.MouseEvent) => {
                        _e.stopPropagation();
                        if (!isRejected) {
                          handleToggleState(idx);
                        }
                      }}
                      className={css({
                        backgroundColor: isRejected ? "#ffeaea" : "transparent",
                        color: isRejected ? "#d32f2f" : "#6b7280",
                        "&:hover": {
                          backgroundColor: isRejected ? "#ffd6d6" : "#f0f1f3",
                        },
                      })}
                    />
                  </div>
                </div>
              </DrawerHeader>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className={css({
                      padding: "0px 8px 0px 8px",
                      overflowY: "scroll",
                      background: "#fff",
                      borderTop: "none",
                    })}
                  >
                    {isRejected ? (
                      <>
                        <RefinementBox>
                          <RefinementLabel>
                            <MdsIcon kind={MdsIconKind.Pen} className={css({ color: "#d32f2f" })} />
                            Give instructions (optional)
                          </RefinementLabel>
                          <RefinementInput
                            type="text"
                            value={col.refinement_phrase}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleFeedback(idx, e.target.value)
                            }
                            placeholder="Tell Mem how to group these notes"
                          />
                        </RefinementBox>
                        <div
                          className={css({
                            color: "#6b7280",
                            fontSize: "14px",
                            lineHeight: "20px",
                            padding: "0px 8px 8px 8px",
                            textAlign: "center",
                          })}
                        >
                          {(col.member_note_ids || []).length} note
                          {(col.member_note_ids || []).length !== 1 ? "s" : ""} moved to Remaining
                          notes
                        </div>
                      </>
                    ) : (
                      <>
                        {visibleNotes.map(noteId => (
                          <NoteRow
                            key={noteId}
                            draggable={!isRejected}
                            className={!isRejected ? "draggable" : ""}
                            onDragStart={(e: React.DragEvent) =>
                              !isRejected && handleDragStart(noteId, idx, e)
                            }
                            onDragOver={handleDragOver}
                            onDrop={e => handleDrop(idx, e)}
                            onMouseEnter={e => handleMouseEnter(noteId, e.currentTarget)}
                            onMouseLeave={handleMouseLeave}
                          >
                            <MdsIcon
                              kind={MdsIconKind.Document}
                              className={css({
                                marginRight: 6,
                                fontSize: 14,
                                color: "#696E7D",
                                flexShrink: 0,
                              })}
                            />
                            <NoteTitle>{getNoteTitle(noteId)}</NoteTitle>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <MdsTooltip
                                config={{
                                  label: "Remove suggestion",
                                  placement: MdsTooltipPlacement.Top,
                                }}
                              >
                                <RemoveButton
                                  type="button"
                                  onClick={(_e: React.MouseEvent) => handleRemoveNote(idx, noteId)}
                                >
                                  <MdsIcon kind={MdsIconKind.CircleMinus} />
                                </RemoveButton>
                              </MdsTooltip>
                            </div>
                          </NoteRow>
                        ))}
                        {isExpanded && hasMoreNotes && (
                          <SeeMoreButton
                            onClick={(_e: React.MouseEvent) => toggleShowAllNotes(idx)}
                          >
                            See {(col.member_note_ids || []).length - visibleNotes.length} more note
                            {(col.member_note_ids || []).length - visibleNotes.length !== 1
                              ? "s"
                              : ""}
                          </SeeMoreButton>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </DrawerCard>
          );
        })}
        {uncategorized.size > 0 && (
          <DrawerCard>
            <DrawerHeader
              onClick={(_e: React.MouseEvent) => handleToggleExpand("-1")}
              style={{ cursor: "pointer" }}
            >
              <DrawerTitleBlock>
                <DrawerTitle $isExpanded={expandedCollections.has("-1")} $isEditable={false}>
                  Remaining notes
                </DrawerTitle>
                <DrawerSubtitle>
                  {uncategorized.size} note{uncategorized.size !== 1 ? "s" : ""}
                </DrawerSubtitle>
              </DrawerTitleBlock>
              <div style={{ marginLeft: 12 }}>
                <MdsIconButton
                  type="button"
                  iconKind={
                    expandedCollections.has("-1") ? MdsIconKind.AngleUp : MdsIconKind.AngleDown
                  }
                  size={MdsIconButtonSize.Small}
                  variant={MdsIconButtonVariant.Transparent}
                  aria-label={expandedCollections.has("-1") ? "Collapse" : "Expand"}
                  onClick={(_e: React.MouseEvent) => {
                    _e.stopPropagation();
                    handleToggleExpand("-1");
                  }}
                />
              </div>
            </DrawerHeader>
            <AnimatePresence>
              {expandedCollections.has("-1") && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className={css({
                    padding: "0px 8px 0px 8px",
                    overflowY: "scroll",
                    background: "#fff",
                    borderTop: "none",
                  })}
                >
                  {Array.from(uncategorized)
                    .slice(0, expandedCollections.has("-1") ? undefined : MAX_VISIBLE)
                    .map(noteId => (
                      <NoteRow
                        key={noteId}
                        $isNew={true}
                        onMouseEnter={e => handleMouseEnter(noteId, e.currentTarget)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <MdsIcon
                          kind={MdsIconKind.Document}
                          className={css({
                            marginRight: 6,
                            fontSize: 14,
                            color: "#9397A5",
                            flexShrink: 0,
                          })}
                        />
                        <NoteTitle>{getNoteTitle(noteId)}</NoteTitle>
                      </NoteRow>
                    ))}
                  {uncategorized.size > MAX_VISIBLE && !expandedCollections.has("-1") && (
                    <Ellipsis>...and {uncategorized.size - MAX_VISIBLE} more</Ellipsis>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </DrawerCard>
        )}
        {hoveredNote && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={store.notes.getNoteObservableById({ noteId: hoveredNote })}
            placement="right"
          />
        )}
        <SummaryRow>
          {/* <SummaryText>{summaryText}</SummaryText> */}
          {/* {uncategorized.size > 0 && (
            <UncategorizedCount>
              {uncategorized.size} note{uncategorized.size !== 1 ? "s" : ""} left to organize
            </UncategorizedCount>
          )} */}
          <ButtonGroup>
            <MDSButton
              type="button"
              color="white"
              size="md"
              onClick={dismissAutoOrganizeCreateCollectionsSession}
            >
              Done organizing
            </MDSButton>
            <MDSButton type="submit" color="contrast" size="md">
              Confirm
            </MDSButton>
          </ButtonGroup>
        </SummaryRow>
      </FormContainer>
    );
  }
);

// Styled components
const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: #696e7d;
  font-size: 14px;
  line-height: 20px;
  padding: 4px 8px;
  margin-bottom: 8px;
  cursor: pointer;
  &:hover {
    color: #3f424d;
    text-decoration: underline;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  padding: 0px 0px 8px 8px;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;

  /* Add styling for the scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const DrawerCard = styled.div`
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 0;
  border: 1px solid #e8e9ed;
  overflow: hidden;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    box-shadow: 0 2px 3px rgba(69, 78, 104, 0.15);
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px 12px 16px;
  cursor: pointer;
  background: #fff;
`;

const DrawerTitleBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const DrawerTitle = styled.div<{
  $isExpanded?: boolean;
  $isRejected?: boolean;
  $isEditable?: boolean;
}>`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  opacity: 1;
  transition:
    opacity 0.2s,
    font-weight 0.2s,
    background-color 0.15s;
  ${({ $isRejected }) => $isRejected && "text-decoration: line-through; color: #9397A5;"}
  ${({ $isEditable }) =>
    $isEditable &&
    `
    cursor: text;
    display: inline-block;
    padding: 2px 4px;
    margin-left: -4px;
    border-radius: 4px;
    &:hover {
      background-color: #f0f1f3;
    }
  `}
`;

const DrawerSubtitle = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 20px;
`;

interface NoteRowProps {
  $isNew?: boolean;
}

const NoteRow = styled.div<NoteRowProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f8fa;
  border-radius: 6px;
  padding: 6px 8px;
  margin: 0 0 4px 0;
  cursor: default;
  width: 100%;
  animation: ${props => (props.$isNew ? "fadeIn 0.3s ease-in" : "none")};

  /* Drag and drop cursor styles */
  &.draggable {
    cursor: grab;
  }
  
  &.draggable:active {
    cursor: grabbing;
  }

  // @keyframes fadeIn {
  //   from {
  //     opacity: 0;
  //     transform: translateY(-4px);
  //   }
  //   to {
  //     opacity: 1;
  //     transform: translateY(0);
  }
  }
`;

const ReadonlyNoteRow = styled(NoteRow)`
  cursor: not-allowed;
  opacity: 0.6;
`;

const NoteTitle = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  color: #3f424d;
  font-weight: 500;
  line-height: 20px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  margin-left: 8px;
  display: flex;
  align-items: center;
  transition: background 0.15s;
  &:hover {
    background: #f0f1f3;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 0;
`;

const RefinementBox = styled.div`
  background: #fff6f6;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
`;

const RefinementLabel = styled.div`
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RefinementInput = styled.input`
  width: 100%;
  min-height: 32px;
  border-radius: 6px;
  border: 1px solid #ffd6d6;
  padding: 8px;
  font-size: 14px;
  line-height: 20px;
  background: #fff;
  color: #3f424d;
  resize: vertical;
`;

const DrawerNoteCount = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  justify-content: flex-end;
`;

const Ellipsis = styled.span`
  color: #6b7280;
  font-size: 0.93em;
  margin-left: 10px;
`;

// Progress Indicator Component
import React from "react";
const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 24px;
`;
const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;
const Circle = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#3864F0" : "transparent")};
  border: 2px solid ${({ active }) => (active ? "#3864F0" : "#A0A4B8")};
  color: ${({ active }) => (active ? "#fff" : "#A0A4B8")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 8px;
`;
const StepLabel = styled.div<{ active: boolean }>`
  color: ${({ active }) => (active ? "#23262F" : "#A0A4B8")};
  font-weight: 600;
  font-size: 12px;
  text-align: center;
`;
const Line = styled.div`
  flex: 1;
  height: 2px;
  background: #a0a4b8;
  margin: -20px -32px 2px -32px;
  border-radius: 2px;
`;

interface ProgressIndicatorProps {
  step: 1 | 2;
}
const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ step }) => (
  <ProgressBar>
    <Step>
      <Circle active={step === 1}>1</Circle>
      <StepLabel active={step === 1}>Add to Existing</StepLabel>
    </Step>
    <Line />
    <Step>
      <Circle active={step === 2}>2</Circle>
      <StepLabel active={step === 2}>Suggest New</StepLabel>
    </Step>
  </ProgressBar>
);
